import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { abandonAuctionCommand, applyEngineeringStrategy, applyForListingCommand, assignAircraft, borrowBankLoan, buildFuelStorageCommand, buildHub, buybackSharesCommand, buyFuelCommand, buyFuelFutureCommand, cancelFuelStorageQueueCommand, closeRoute, completeAuditCommand, completeListingCommand, declarePlayerDividendCommand, dismissAssistant, extendBankLoan, extendFuelStorageFacilityCommand, investInVentureRoundCommand, issueSharesCommand, leaseAircraft, maintainAircraft, maintainLowConditionAircraft, minimizeAuctionCommand, openRoute, payDownBankLoan, payOffLoan, placeAuctionBidCommand, placeFixedAuctionBidCommand, playerBuyCompanySharesCommand, playerBuySharesCommand, playerSellCompanySharesCommand, playerSellSharesCommand, purchaseAircraft, recruitAssistant, refinanceLoan, renameCompany, replaceAircraftOnRoute, resolveAuctionCommand, resolveMarketEventResponse, returnLeasedAircraft, runLowMarginScan, sellAircraft, sellFuelCommand, sellFuelFutureCommand, setAircraftCabinConfig, setAircraftCrewSalaryModifier, setAircraftFareRatio, setAllAircraftCrewSalaryModifier, setAircraftSpeedModifier, setHubSpecialization, setRouteFareRatio, shortFuelFutureCommand, startPlayerVentureRoundCommand, unassignAircraft, upgradeHub } from './commands';
import { QUIZ_QUESTIONS } from '../data/quizQuestions';
import type { AcquisitionType, GameState, HubAsset, PurchaseMethod } from './types';
import { createSave, deleteSave, listSaves, loadGame, loadSave, resetStoredGame, saveGame, type SaveSummary } from '../storage/db';
import type { NewGameOptions } from './scenarios';
import { simulateWeek } from '../simulation/simulateWeek';
import { computeAdvancedWeek } from './advanceWeek';
import { applyQuizResult, applyResearchResult } from './quizFlow';
import { addAdvancedAiManager as addAdvancedAiManagerCommand, addAiManagers as addAiManagersCommand, addEliteAiManager as addEliteAiManagerCommand, addStrategicAiManager as addStrategicAiManagerCommand } from './aiManagers';
import { completeTutorialIfReady } from './tutorial';
import { updateAchievements } from './achievements';
import { money } from '../ui/format';
import type { EngineeringStrategy } from '../assistants/flightSpeed';
import { chooseCity as apiChooseCity, createRoom as apiCreateRoom, fetchRoom, joinRoom as apiJoinRoom, leaveRoom as apiLeaveRoom, setPlayerReady as apiSetPlayerReady, startRoomGame as apiStartRoomGame, submitState, updateRoomSettings as apiUpdateRoomSettings, type RoomSettings, type RoomSnapshot } from '../net/roomClient';

const ROOM_SESSION_KEY = 'noflight-room-session';
const ROOM_POLL_INTERVAL_MS = 1000;

function automaticMaintenanceNotice(previous: GameState, next: GameState): string | null {
  if (next.week <= previous.week) return null;
  const maintenanceEntries = next.ledger
    .slice(previous.ledger.length)
    .filter((entry) => entry.label.endsWith('维护检修'));
  if (!maintenanceEntries.length) return null;
  const totalCost = maintenanceEntries.reduce((sum, entry) => sum - Math.min(0, entry.cashAmount), 0);
  return `已自动维护 ${maintenanceEntries.length} 架飞机，机况已恢复，扣除 ${money(totalCost)}`;
}

interface RoomSession {
  roomId: string;
  playerId: string;
}

interface GameContextValue {
  game: GameState | null;
  loading: boolean;
  notice: string | null;
  clearNotice: () => void;
  room: RoomSnapshot | null;
  /** 房间模式下已提交本周数据（或房间结算中），所有写入被锁定，只能查看 */
  roomLocked: boolean;
  roomSubmitting: boolean;
  createRoom: (name: string) => Promise<void>;
  joinRoom: (roomId: string, name: string) => Promise<void>;
  leaveRoom: () => Promise<void>;
  updateRoomSettings: (settings: RoomSettings) => Promise<void>;
  chooseCity: (city: string) => Promise<void>;
  setRoomReady: (ready: boolean) => Promise<void>;
  startRoomGame: () => Promise<void>;
  buyAircraft: (typeId: string, acquisition: AcquisitionType, method: PurchaseMethod) => void;
  leaseAircraft: (typeId: string) => void;
  openRoute: (originId: string, destinationId: string, aircraftId: string) => void;
  assignAircraft: (routeId: string, aircraftId: string) => void;
  unassignAircraft: (routeId: string, aircraftId: string) => void;
  replaceAircraftOnRoute: (routeId: string, fromAircraftId: string, toAircraftId: string) => void;
  closeRoute: (routeId: string) => void;
  buildHub: (airportId: string) => void;
  upgradeHub: (airportId: string) => void;
  setHubSpecialization: (airportId: string, specialization: HubAsset['specialization']) => void;
  sellAircraft: (aircraftId: string) => void;
  returnLeasedAircraft: (aircraftId: string) => void;
  payOffLoan: (loanId: string) => void;
  payDownBankLoan: (loanId: string, amount: number) => void;
  refinanceLoan: (loanId: string) => void;
  extendBankLoan: (loanId: string) => void;
  borrowBankLoan: (amount: number) => void;
  maintainAircraft: (aircraftId: string) => void;
  maintainLowConditionAircraft: () => void;
  setAircraftSpeedModifier: (aircraftId: string, modifier: number) => void;
  applyEngineeringStrategy: (strategy: EngineeringStrategy) => void;
  recruitAssistant: (assistantId: string) => void;
  dismissAssistant: (assistantId: string) => void;
  completeAudit: () => void;
  applyForListing: () => void;
  completeListing: (issueRatio: number) => void;
  issueShares: (quantity: number) => void;
  buybackShares: (quantity: number) => void;
  playerSellShares: (quantity: number) => void;
  playerBuyShares: (quantity: number) => void;
  playerSellCompanyShares: (companyId: string, quantity: number) => void;
  playerBuyCompanyShares: (companyId: string, quantity: number) => void;
  declarePlayerDividend: (period: 'quarterly' | 'annual', payoutRatio: number) => void;
  startPlayerVentureRound: (targetAmount: number, secondarySaleAmount: number) => void;
  investInVentureRound: (roundId: string, amount: number) => void;
  buildFuelStorage: (count?: number) => void;
  cancelFuelStorageQueue: () => void;
  extendFuelStorageFacility: (facilityId: string) => void;
  buyFuel: (gallons: number) => void;
  buyFuelFuture: (gallons: number, deliveryWeeks: number) => void;
  shortFuelFuture: (gallons: number, deliveryWeeks: number) => void;
  sellFuel: (gallons: number) => void;
  sellFuelFuture: (futureId: string) => void;
  setAircraftCabinConfig: (aircraftId: string, cabinConfig: { economy: number; business: number; first: number }) => void;
  setAircraftCrewSalaryModifier: (aircraftId: string, modifier: number) => void;
  setAircraftFareRatio: (aircraftId: string, fareRatio: number) => void;
  setAllAircraftCrewSalaryModifier: (modifier: number) => void;
  setRouteFareRatio: (routeId: string, fareRatio: number) => void;
  placeAuctionBid: (incrementPercent: number) => void;
  placeFixedAuctionBid: (amount: number) => void;
  abandonAuction: () => void;
  minimizeAuction: (minimized: boolean) => void;
  resolveAuction: () => void;
  runLowMarginScan: () => void;
  addAiManagers: (count: number) => void;
  addAdvancedAiManager: () => void;
  addStrategicAiManager: () => void;
  addEliteAiManager: () => void;
  advanceWeek: () => void;
  resolveQuiz: (correct: boolean) => void;
  openResearchOpportunity: () => void;
  snoozeResearchOpportunity: () => void;
  dismissResearchOpportunity: () => void;
  resolveResearch: (answerIndex: number) => void;
  setQuizModeEnabled: (enabled: boolean) => void;
  respondToMarketEvent: (eventId: string, optionId: string) => void;
  renameCompany: (name: string) => void;
  listSaves: () => Promise<SaveSummary[]>;
  switchSave: (id: string) => Promise<void>;
  createNewSave: (options?: NewGameOptions) => Promise<void>;
  removeSave: (id: string) => Promise<void>;
  resetGame: (options?: NewGameOptions) => Promise<void>;
}

const GameContext = createContext<GameContextValue | undefined>(undefined);

function readRoomSession(): RoomSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(ROOM_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RoomSession;
    return parsed?.roomId && parsed?.playerId ? parsed : null;
  } catch {
    return null;
  }
}

function writeRoomSession(session: RoomSession | null): void {
  if (typeof window === 'undefined') return;
  if (session) window.localStorage.setItem(ROOM_SESSION_KEY, JSON.stringify(session));
  else window.localStorage.removeItem(ROOM_SESSION_KEY);
}

export function GameProvider({ children }: { children: ReactNode }) {
  const [game, setGame] = useState<GameState | null>(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const [roomSession, setRoomSession] = useState<RoomSession | null>(() => readRoomSession());
  const [room, setRoom] = useState<RoomSnapshot | null>(null);
  const [roomSubmitting, setRoomSubmitting] = useState(false);
  const roomSessionRef = useRef(roomSession);
  roomSessionRef.current = roomSession;
  const roomRef = useRef(room);
  roomRef.current = room;

  // 初始载入：有房间会话则以服务端数据为准，否则读本地存档
  useEffect(() => {
    const session = readRoomSession();
    if (!session) {
      loadGame().then(setGame).catch(() => setNotice('无法读取本地存档')).finally(() => setLoading(false));
      return;
    }
    fetchRoom(session.roomId, session.playerId)
      .then(({ room: snapshot }) => {
        if (!snapshot.me) throw new Error('你不在这个房间中');
        setRoom(snapshot);
        // 大厅阶段还没有房间存档，先显示本地存档；游戏开始后轮询会自动切换
        if (snapshot.me.state) setGame(snapshot.me.state);
        else return loadGame().then(setGame).catch(() => setNotice('无法读取本地存档'));
      })
      .catch((error) => {
        writeRoomSession(null);
        setRoomSession(null);
        setNotice(error instanceof Error ? `${error.message}，已回到本地单机模式` : '无法连接房间，已回到本地单机模式');
        return loadGame().then(setGame).catch(() => setNotice('无法读取本地存档'));
      })
      .finally(() => setLoading(false));
  }, []);

  // 房间模式：每秒轮询房间状态；服务端推进完成后自动采用新状态并解锁
  useEffect(() => {
    if (!roomSession) return;
    let cancelled = false;
    const poll = async () => {
      const session = roomSessionRef.current;
      if (!session) return;
      try {
        const { room: snapshot } = await fetchRoom(session.roomId, session.playerId);
        if (cancelled || !roomSessionRef.current) return;
        setRoom(snapshot);
        const incoming = snapshot.me?.state;
        if (incoming) {
          setGame((current) => (!current || current.id !== incoming.id || incoming.week > current.week ? incoming : current));
        }
      } catch {
        // 单次轮询失败（网络抖动等）下个周期再试
      }
    };
    void poll();
    const timer = window.setInterval(() => void poll(), ROOM_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [roomSession]);

  // 本地自动存档：仅单机模式；房间模式以服务端为准
  useEffect(() => {
    if (!game || loading || roomSession) return;
    const timer = window.setTimeout(() => saveGame(game).catch(() => setNotice('本地存档失败，请检查浏览器存储权限')), 180);
    return () => window.clearTimeout(timer);
  }, [game, loading, roomSession]);

  const roomLocked = !!roomSession && (!room || room.phase !== 'playing' || !!room.me?.ready || roomSubmitting);

  const execute = useCallback((command: (state: GameState) => GameState) => {
    setGame((current) => {
      if (!current) return current;
      try {
        const next = updateAchievements(completeTutorialIfReady(command(current)));
        setNotice(automaticMaintenanceNotice(current, next));
        return next;
      } catch (error) {
        setNotice(error instanceof Error ? error.message : '操作失败');
        return current;
      }
    });
  }, []);

  // 房间模式下允许在“进行中且未提交”时执行的命令；锁定后只能查看
  const executeRoomAware = useCallback((command: (state: GameState) => GameState) => {
    const session = roomSessionRef.current;
    if (!session) {
      execute(command);
      return;
    }
    const currentRoom = roomRef.current;
    if (!currentRoom || currentRoom.phase !== 'playing' || currentRoom.me?.ready) {
      setNotice('已提交本周数据，等待其他玩家完成操作；当前只能查看，不能修改');
      return;
    }
    setGame((current) => {
      if (!current) return current;
      try {
        const next = updateAchievements(completeTutorialIfReady(command(current)));
        setNotice(automaticMaintenanceNotice(current, next));
        return next;
      } catch (error) {
        setNotice(error instanceof Error ? error.message : '操作失败');
        return current;
      }
    });
  }, [execute]);

  const submitWeekToServer = useCallback(async () => {
    const session = roomSessionRef.current;
    if (!session) return;
    setRoomSubmitting(true);
    try {
      const current = await new Promise<GameState | null>((resolve) => {
        setGame((state) => {
          resolve(state);
          return state;
        });
      });
      if (!current) return;
      const { room: snapshot } = await submitState(session.roomId, session.playerId, current);
      setRoom(snapshot);
      const incoming = snapshot.me?.state;
      if (incoming) {
        setNotice(automaticMaintenanceNotice(current, incoming));
        setGame((prev) => (!prev || incoming.week > prev.week ? incoming : prev));
      }
    } catch (error) {
      setNotice(error instanceof Error ? error.message : '提交本周数据失败');
    } finally {
      setRoomSubmitting(false);
    }
  }, []);

  const value = useMemo<GameContextValue>(() => ({
    game,
    loading,
    notice,
    clearNotice: () => setNotice(null),
    room,
    roomLocked,
    roomSubmitting,
    createRoom: async (name) => {
      try {
        const result = await apiCreateRoom(name);
        const session = { roomId: result.roomId, playerId: result.playerId };
        writeRoomSession(session);
        setRoomSession(session);
        setRoom(result.room);
        if (result.room.me?.state) setGame(result.room.me.state);
        setNotice(null);
      } catch (error) {
        setNotice(error instanceof Error ? error.message : '创建房间失败');
        throw error;
      }
    },
    joinRoom: async (roomId, name) => {
      try {
        const result = await apiJoinRoom(roomId, name);
        const session = { roomId: result.roomId, playerId: result.playerId };
        writeRoomSession(session);
        setRoomSession(session);
        setRoom(result.room);
        if (result.room.me?.state) setGame(result.room.me.state);
        setNotice(null);
      } catch (error) {
        setNotice(error instanceof Error ? error.message : '加入房间失败');
        throw error;
      }
    },
    leaveRoom: async () => {
      const session = roomSessionRef.current;
      writeRoomSession(null);
      setRoomSession(null);
      setRoom(null);
      if (session) {
        try {
          await apiLeaveRoom(session.roomId, session.playerId);
        } catch {
          // 离开房间失败不影响本地退出
        }
      }
      setLoading(true);
      try {
        setGame(await loadGame());
      } catch {
        setNotice('无法读取本地存档');
      } finally {
        setLoading(false);
      }
    },
    updateRoomSettings: async (settings) => {
      const session = roomSessionRef.current;
      if (!session) return;
      try {
        const { room: snapshot } = await apiUpdateRoomSettings(session.roomId, session.playerId, settings);
        setRoom(snapshot);
        setNotice(null);
      } catch (error) {
        setNotice(error instanceof Error ? error.message : '保存房间设置失败');
        throw error;
      }
    },
    chooseCity: async (city) => {
      const session = roomSessionRef.current;
      if (!session) return;
      try {
        const { room: snapshot } = await apiChooseCity(session.roomId, session.playerId, city);
        setRoom(snapshot);
        setNotice(null);
      } catch (error) {
        setNotice(error instanceof Error ? error.message : '选择开局城市失败');
        throw error;
      }
    },
    setRoomReady: async (ready) => {
      const session = roomSessionRef.current;
      if (!session) return;
      try {
        const { room: snapshot } = await apiSetPlayerReady(session.roomId, session.playerId, ready);
        setRoom(snapshot);
        setNotice(null);
      } catch (error) {
        setNotice(error instanceof Error ? error.message : '准备状态更新失败');
        throw error;
      }
    },
    startRoomGame: async () => {
      const session = roomSessionRef.current;
      if (!session) return;
      try {
        const { room: snapshot } = await apiStartRoomGame(session.roomId, session.playerId);
        setRoom(snapshot);
        if (snapshot.me?.state) setGame(snapshot.me.state);
        setNotice(null);
      } catch (error) {
        setNotice(error instanceof Error ? error.message : '开始游戏失败');
        throw error;
      }
    },
    buyAircraft: (typeId, acquisition, method) => executeRoomAware((state) => purchaseAircraft(state, typeId, acquisition, method)),
    leaseAircraft: (typeId) => executeRoomAware((state) => leaseAircraft(state, typeId)),
    openRoute: (originId, destinationId, aircraftId) => executeRoomAware((state) => openRoute(state, originId, destinationId, aircraftId)),
    assignAircraft: (routeId, aircraftId) => executeRoomAware((state) => assignAircraft(state, routeId, aircraftId)),
    unassignAircraft: (routeId, aircraftId) => executeRoomAware((state) => unassignAircraft(state, routeId, aircraftId)),
    replaceAircraftOnRoute: (routeId, fromAircraftId, toAircraftId) => executeRoomAware((state) => replaceAircraftOnRoute(state, routeId, fromAircraftId, toAircraftId)),
    closeRoute: (routeId) => executeRoomAware((state) => closeRoute(state, routeId)),
    buildHub: (airportId) => executeRoomAware((state) => buildHub(state, airportId)),
    upgradeHub: (airportId) => executeRoomAware((state) => upgradeHub(state, airportId)),
    setHubSpecialization: (airportId, specialization) => executeRoomAware((state) => setHubSpecialization(state, airportId, specialization)),
    sellAircraft: (aircraftId) => executeRoomAware((state) => sellAircraft(state, aircraftId)),
    returnLeasedAircraft: (aircraftId) => executeRoomAware((state) => returnLeasedAircraft(state, aircraftId)),
    payOffLoan: (loanId) => executeRoomAware((state) => payOffLoan(state, loanId)),
    payDownBankLoan: (loanId, amount) => executeRoomAware((state) => payDownBankLoan(state, loanId, amount)),
    refinanceLoan: (loanId) => executeRoomAware((state) => refinanceLoan(state, loanId)),
    extendBankLoan: (loanId) => executeRoomAware((state) => extendBankLoan(state, loanId)),
    borrowBankLoan: (amount) => executeRoomAware((state) => borrowBankLoan(state, amount)),
    maintainAircraft: (aircraftId) => executeRoomAware((state) => maintainAircraft(state, aircraftId)),
    maintainLowConditionAircraft: () => executeRoomAware((state) => maintainLowConditionAircraft(state)),
    setAircraftSpeedModifier: (aircraftId, modifier) => executeRoomAware((state) => setAircraftSpeedModifier(state, aircraftId, modifier)),
    applyEngineeringStrategy: (strategy) => executeRoomAware((state) => applyEngineeringStrategy(state, strategy)),
    recruitAssistant: (assistantId) => executeRoomAware((state) => recruitAssistant(state, assistantId)),
    dismissAssistant: (assistantId) => executeRoomAware((state) => dismissAssistant(state, assistantId)),
    completeAudit: () => executeRoomAware((state) => completeAuditCommand(state)),
    applyForListing: () => executeRoomAware((state) => applyForListingCommand(state)),
    completeListing: (issueRatio) => executeRoomAware((state) => completeListingCommand(state, issueRatio)),
    issueShares: (quantity) => executeRoomAware((state) => issueSharesCommand(state, quantity)),
    buybackShares: (quantity) => executeRoomAware((state) => buybackSharesCommand(state, quantity)),
    playerSellShares: (quantity) => executeRoomAware((state) => playerSellSharesCommand(state, quantity)),
    playerBuyShares: (quantity) => executeRoomAware((state) => playerBuySharesCommand(state, quantity)),
    playerSellCompanyShares: (companyId, quantity) => executeRoomAware((state) => playerSellCompanySharesCommand(state, companyId, quantity)),
    playerBuyCompanyShares: (companyId, quantity) => executeRoomAware((state) => playerBuyCompanySharesCommand(state, companyId, quantity)),
    declarePlayerDividend: (period, payoutRatio) => executeRoomAware((state) => declarePlayerDividendCommand(state, period, payoutRatio)),
    startPlayerVentureRound: (targetAmount, secondarySaleAmount) => executeRoomAware((state) => startPlayerVentureRoundCommand(state, targetAmount, secondarySaleAmount)),
    investInVentureRound: (roundId, amount) => executeRoomAware((state) => investInVentureRoundCommand(state, roundId, amount)),
    buildFuelStorage: (count) => executeRoomAware((state) => buildFuelStorageCommand(state, count)),
    cancelFuelStorageQueue: () => executeRoomAware((state) => cancelFuelStorageQueueCommand(state)),
    extendFuelStorageFacility: (facilityId) => executeRoomAware((state) => extendFuelStorageFacilityCommand(state, facilityId)),
    buyFuel: (gallons) => executeRoomAware((state) => buyFuelCommand(state, gallons)),
    buyFuelFuture: (gallons, deliveryWeeks) => executeRoomAware((state) => buyFuelFutureCommand(state, gallons, deliveryWeeks)),
    shortFuelFuture: (gallons, deliveryWeeks) => executeRoomAware((state) => shortFuelFutureCommand(state, gallons, deliveryWeeks)),
    sellFuel: (gallons) => executeRoomAware((state) => sellFuelCommand(state, gallons)),
    sellFuelFuture: (futureId) => executeRoomAware((state) => sellFuelFutureCommand(state, futureId)),
    setAircraftCabinConfig: (aircraftId, cabinConfig) => executeRoomAware((state) => setAircraftCabinConfig(state, aircraftId, cabinConfig)),
    setAircraftCrewSalaryModifier: (aircraftId, modifier) => executeRoomAware((state) => setAircraftCrewSalaryModifier(state, aircraftId, modifier)),
    setAircraftFareRatio: (aircraftId, fareRatio) => executeRoomAware((state) => setAircraftFareRatio(state, aircraftId, fareRatio)),
    setAllAircraftCrewSalaryModifier: (modifier) => executeRoomAware((state) => setAllAircraftCrewSalaryModifier(state, modifier)),
    setRouteFareRatio: (routeId, fareRatio) => executeRoomAware((state) => setRouteFareRatio(state, routeId, fareRatio)),
    placeAuctionBid: (incrementPercent) => executeRoomAware((state) => placeAuctionBidCommand(state, incrementPercent)),
    placeFixedAuctionBid: (amount) => executeRoomAware((state) => placeFixedAuctionBidCommand(state, amount)),
    abandonAuction: () => executeRoomAware((state) => abandonAuctionCommand(state)),
    minimizeAuction: (minimized) => executeRoomAware((state) => minimizeAuctionCommand(state, minimized)),
    resolveAuction: () => executeRoomAware((state) => resolveAuctionCommand(state)),
    runLowMarginScan: () => executeRoomAware((state) => runLowMarginScan(state)),
    addAiManagers: (count) => {
      if (roomSessionRef.current) setNotice('联机房间的 AI 由房主在开局前统一设置，游戏开始后不能调整');
      else executeRoomAware((state) => addAiManagersCommand(state, count));
    },
    addAdvancedAiManager: () => {
      if (roomSessionRef.current) setNotice('联机房间的 AI 由房主在开局前统一设置，游戏开始后不能调整');
      else executeRoomAware((state) => addAdvancedAiManagerCommand(state));
    },
    addStrategicAiManager: () => {
      if (roomSessionRef.current) setNotice('联机房间的 AI 由房主在开局前统一设置，游戏开始后不能调整');
      else executeRoomAware((state) => addStrategicAiManagerCommand(state));
    },
    addEliteAiManager: () => {
      if (roomSessionRef.current) setNotice('联机房间的 AI 由房主在开局前统一设置，游戏开始后不能调整');
      else executeRoomAware((state) => addEliteAiManagerCommand(state));
    },
    advanceWeek: () => {
      const session = roomSessionRef.current;
      if (session) {
        if (!game || game.gameOver || game.activeAuction) return;
        if (roomLocked) return;
        void submitWeekToServer();
        return;
      }
      execute((state) => computeAdvancedWeek(state));
    },
    resolveQuiz: (correct: boolean) => executeRoomAware((state) => {
      if (state.gameOver) return state;
      if (state.activeAuction) return state;
      if (!state.pendingQuiz) return state;
      const afterQuiz = applyQuizResult(state, correct);
      afterQuiz.pendingQuiz = false;
      afterQuiz.lastQuizWeek = state.week;
      if (roomSessionRef.current) {
        // 房间模式：周推进只由服务端执行，本地不再重复模拟
        return { ...afterQuiz, quizCounter: 0 };
      }
      return { ...simulateWeek(afterQuiz), quizCounter: 0 };
    }),
    openResearchOpportunity: () => executeRoomAware((state) => {
      if (!state.researchOpportunity) return state;
      return { ...state, researchOpportunity: { ...state.researchOpportunity, status: 'available' } };
    }),
    snoozeResearchOpportunity: () => executeRoomAware((state) => {
      if (!state.researchOpportunity) return state;
      return { ...state, researchOpportunity: { ...state.researchOpportunity, status: 'snoozed' } };
    }),
    dismissResearchOpportunity: () => executeRoomAware((state) => ({
      ...state,
      researchOpportunity: undefined,
      lastQuizWeek: state.week,
      quizCounter: 0,
      pendingQuiz: false,
    })),
    resolveResearch: (answerIndex: number) => executeRoomAware((state) => {
      const opportunity = state.researchOpportunity;
      if (!opportunity || state.gameOver || state.activeAuction) return state;
      const question = QUIZ_QUESTIONS.find((item) => item.id === opportunity.questionId);
      if (!question) return { ...state, researchOpportunity: undefined };
      return applyResearchResult(state, answerIndex === question.answer);
    }),
    setQuizModeEnabled: (enabled) => executeRoomAware((state) => ({
      ...state,
      quizModeEnabled: enabled,
      ...(enabled ? {} : { researchOpportunity: undefined, pendingQuiz: false, quizCounter: 0, lastQuizWeek: state.week }),
    })),
    respondToMarketEvent: (eventId, optionId) => executeRoomAware((state) => resolveMarketEventResponse(state, eventId, optionId)),
    renameCompany: (name) => executeRoomAware((state) => renameCompany(state, name)),
    listSaves: () => listSaves(),
    switchSave: async (id) => {
      if (game?.id === id) return;
      setLoading(true);
      try {
        const loaded = await loadSave(id);
        setGame(loaded);
        setNotice(null);
      } catch (error) {
        setNotice(error instanceof Error ? error.message : '载入存档失败');
      } finally {
        setLoading(false);
      }
    },
    createNewSave: async (options) => {
      setLoading(true);
      try {
        const initial = await createSave(options);
        setGame(initial);
        setNotice(null);
      } catch (error) {
        setNotice(error instanceof Error ? error.message : '创建存档失败');
        throw error;
      } finally {
        setLoading(false);
      }
    },
    removeSave: async (id) => {
      try {
        await deleteSave(id);
      } catch (error) {
        setNotice(error instanceof Error ? error.message : '删除存档失败');
        throw error;
      }
    },
    resetGame: async (options) => {
      setLoading(true);
      const initial = await resetStoredGame(options);
      setGame(initial);
      setLoading(false);
      setNotice(null);
    },
  }), [execute, executeRoomAware, submitWeekToServer, roomLocked, room, roomSubmitting, game, loading, notice]);

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export function useGame(): GameContextValue {
  const value = useContext(GameContext);
  if (!value) throw new Error('useGame 必须在 GameProvider 内使用');
  return value;
}
